import { useEffect } from "react";

// Messages that can be sent to Godot
export const GODOT_MESSAGE = {
  INIT_BOARD: "godot:init_board",
  TOGGLE_TOOL: "godot:toggle_tool",
  TOGGLE_ZOOM: "godot:toggle_zoom",
  TOGGLE_ROTATE: "godot:toggle_rotate",
  SET_DIMENSIONS: "godot:set_dimensions",
  SET_AUDIO_MUTE: "godot:set_audio_mute",
  SET_VICTORY_IMAGE: "godot:set_victory_image",
  DEBUG_TRIGGER_VICTORY: "godot:debug_trigger_victory",
} as const;
export type GodotMessage = (typeof GODOT_MESSAGE)[keyof typeof GODOT_MESSAGE];

// Events dispatched from Godot that can be listened to in React
export const GODOT_EVENT = {
  BOARD_INITIALIZED: "godot:board_initialized",
  GAMEPLAY_START: "godot:gameplay_start",
  BOARD_STATE_CHANGED: "godot:board_state_changed",
  SOLVED: "godot:solved",
  VICTORY: "godot:victory",
};
export type GodotEvent = (typeof GODOT_EVENT)[keyof typeof GODOT_EVENT];

export interface BoardInitializedEvent {
  aspectRatio: number;
}

export interface BoardStateEvent {
  boardState: string;
}

export function sendGodotMessage(
  message: GodotMessage,
  arg1: string = "",
  arg2: string = "",
  arg3: string = "",
  arg4: string = "",
) {
  // console.log(`Attempting to send message to Godot: ${message}`);
  if (window.godot_handler) {
    window.godot_handler(message, arg1, arg2, arg3, arg4);
  } else {
    console.log("Godot instance not found.");
  }
}

export function useGodotListener<T>(
  event: GodotEvent,
  callback: (eventData: T) => void,
) {
  useEffect(() => {
    const safeCallback = (e: Event) => {
      const customEvent = e as CustomEvent;

      // On the Godot side, we convert the event detail to a JSON string before
      // sending the event. Here we validate and then convert it to a JS object.
      if (typeof customEvent.detail !== "string") {
        console.log("Invalid event");
        return;
      }

      try {
        const data = JSON.parse(customEvent.detail) as T;
        callback(data);
      } catch (err) {
        console.error("Could not parse Godot event data:", err);
      }
    };

    window.addEventListener(event, safeCallback);

    return () => {
      window.removeEventListener(event, safeCallback);
    };
  }, [callback]);
}
